import posthog from 'posthog-js'
import { defineNuxtPlugin } from '#app'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const publicKey = config.public.POSTHOG_PUBLIC_KEY

  // Production only — no key means no PostHog
  if (!publicKey || process.env.NODE_ENV !== 'production') {
    const noop = () => {}
    const noopPosthog = new Proxy({} as typeof posthog, {
      get: () => noop,
    })
    return { provide: { posthog: noopPosthog } }
  }

  // MULTI-TENANT SAFETY: See "Multi-Tenant Safety Audit" in the plan.
  const hostname = window.location.hostname

  posthog.init(publicKey, {
    api_host: 'https://us.i.posthog.com',

    // --- Multi-tenant cookie isolation (CRITICAL) ---
    // PostHog defaults cross_subdomain_cookie to true, which scopes cookies
    // to the parent domain (.mibuenprovecho.com). This would bleed sessions
    // across tenant subdomains. Setting to false scopes to exact hostname.
    cross_subdomain_cookie: false,
    persistence: 'localStorage+cookie',
    persistence_name: `bp_${hostname}`, // unique storage key per tenant

    // --- Capture settings ---
    capture_pageview: 'history_change', // SPA-aware: captures on pushState/popstate, not just init
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,

    // --- Session replay ---
    session_recording: {
      // Only mask password inputs; all other fields visible per business decision
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      // Sanitize network requests captured in session replay.
      maskCapturedNetworkRequestFn: (request) => {
        // Strip Authorization header
        if (request.requestHeaders) {
          delete request.requestHeaders['Authorization']
          delete request.requestHeaders['authorization']
        }

        // Mask sensitive fields in request body
        if (request.requestBody) {
          try {
            const body = typeof request.requestBody === 'string'
              ? JSON.parse(request.requestBody)
              : request.requestBody
            const sensitiveFields = ['password', 'token', 'secret', 'passwordConfirmation']
            let modified = false
            for (const field of sensitiveFields) {
              if (body[field]) {
                body[field] = '****'
                modified = true
              }
            }
            if (modified) {
              request.requestBody = JSON.stringify(body)
            }
          } catch {
            // Non-JSON body — leave as-is
          }
        }

        return request
      },
    },
  })

  return { provide: { posthog } }
})
